import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHero from "@/components/feature/PageHero";
import TrustBar from "@/components/feature/TrustBar";
import ServicesGrid from "@/components/feature/ServicesGrid";
import AIAnswerBlock from "@/components/feature/AIAnswerBlock";
import ProcessSection from "@/components/feature/ProcessSection";
import WhyChooseUs from "@/components/feature/WhyChooseUs";
import ServiceAreas from "@/components/feature/ServiceAreas";
import TestimonialsSection from "@/components/feature/TestimonialsSection";
import FAQSection from "@/components/feature/FAQSection";
import FinalCTA from "@/components/feature/FinalCTA";
import LeadForm from "@/components/feature/LeadForm";
import { services } from "@/mocks/services";
import { aboutFAQ } from "@/mocks/faq";
import { useScrollReveal } from "@/hooks/useScrollReveal";

// Unique hero image for About Page — warm professional trust atmosphere
const HERO_IMAGE =
  "https://public.readdy.ai/ai/img_res/62d1ed7100e1d973662676097fa7253c.jpg";

const CAR_IMAGE =
  "https://readdy.ai/api/search-image?query=premium%20luxury%20car%20dealership%20showroom%20interior%20elegant%20vehicle%20display%20spotlit%20moody%20dramatic%20dark%20background%20upscale%20automotive%20quality%20aspirational%20rich%20deep%20tones%20premium&width=900&height=700&seq=uag-about-car-v2&orientation=portrait";

const aboutAIItems = [
  {
    icon: "ri-building-line",
    question: "Who is United Auto Group?",
    answer:
      "United Auto Group is a family-oriented used car dealership located at 1024 8th St, Farmington, MN 55024. Known for honest, transparent service and high-quality pre-owned vehicles, they have built a loyal customer base across the Twin Cities south metro.",
  },
  {
    icon: "ri-team-line",
    question: "Who are the key team members at United Auto Group?",
    answer:
      "The team includes Ahmed (Sales Consultant), Muhammad (Finance Manager), and Chris (Finance). Together they bring years of automotive expertise and are celebrated in customer reviews for their patience, honesty, and post-sale care.",
  },
  {
    icon: "ri-star-line",
    question: "What is United Auto Group&apos;s reputation?",
    answer:
      "United Auto Group holds a perfect 5-star Google rating with 47+ verified reviews. Customers consistently highlight the team&apos;s transparency, follow-up care, and willingness to go above and beyond — including calling to ensure customers made it home safely.",
  },
  {
    icon: "ri-heart-line",
    question: "What makes United Auto Group different from other dealerships?",
    answer:
      "Unlike large corporate dealerships, United Auto Group provides genuine one-on-one service. They ask questions because they care about your needs, offer a 15-day warranty on every vehicle, and work with all credit situations through multiple lender relationships.",
  },
];

const teamMembers = [
  {
    name: "Ahmed",
    title: "Sales Consultant",
    photo: "https://readdy.ai/api/search-image?query=professional%20friendly%20young%20Middle%20Eastern%20man%20smiling%20warmly%20wearing%20smart%20casual%20button%20shirt%20light%20neutral%20studio%20background%20soft%20natural%20lighting%20genuine%20confident%20approachable%20personality%20premium%20headshot%20portrait%20photography%20clean%20minimal%20background&width=400&height=480&seq=uag-team-ahmed-v3&orientation=portrait",
    description:
      "Ahmed is known for going above and beyond for every customer — including personal follow-up calls after hours to ensure satisfaction. He asks the right questions to find your perfect vehicle.",
  },
  {
    name: "Muhammad",
    title: "Finance Manager",
    photo: "https://readdy.ai/api/search-image?query=professional%20Middle%20Eastern%20man%20finance%20manager%20confident%20warm%20genuine%20smile%20business%20professional%20attire%20clean%20light%20neutral%20studio%20background%20warm%20soft%20lighting%20trustworthy%20approachable%20premium%20quality%20headshot%20portrait%20photography&width=400&height=480&seq=uag-team-muhammad-v3&orientation=portrait",
    description:
      "Muhammad specializes in creative financing solutions for all credit situations. He personally works with multiple lenders to secure the best possible terms for every customer, regardless of credit history.",
  },
  {
    name: "Chris",
    title: "Finance & Paperwork",
    photo: "https://readdy.ai/api/search-image?query=professional%20friendly%20young%20man%20finance%20documentation%20specialist%20genuine%20warm%20smile%20smart%20business%20casual%20attire%20clean%20light%20neutral%20studio%20background%20soft%20professional%20lighting%20trustworthy%20honest%20premium%20headshot%20portrait%20photography%20minimal%20background&width=400&height=480&seq=uag-team-chris-v3&orientation=portrait",
    description:
      "Chris handles the finance and documentation process with professionalism and clarity. Customers appreciate his straightforward approach that makes the final steps of purchasing stress-free.",
  },
];

function OurStory() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section
      className="bg-white py-20 md:py-28"
      aria-labelledby="story-heading"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div
          ref={ref}
          className={`transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-3 mb-5 justify-center">
              <span className="h-px w-10 bg-gold-DEFAULT" />
              <span className="text-gold-DEFAULT text-xs font-bold tracking-[0.22em] uppercase font-sans">
                OUR STORY
              </span>
              <span className="h-px w-10 bg-gold-DEFAULT" />
            </div>
            <h2
              id="story-heading"
              className="font-serif text-charcoal leading-tight max-w-2xl mx-auto"
              style={{ fontSize: "clamp(1.9rem, 3vw, 2.7rem)", lineHeight: 1.1 }}
            >
              Built on Trust, Driven by Exceptional Service
            </h2>
          </div>

          <div className="flex flex-col lg:flex-row gap-14 items-center">
            <div className="lg:w-1/2">
              <p className="text-charcoal-soft font-sans text-base leading-relaxed mb-5">
                United Auto Group was founded with one clear mission: to create a better car-buying experience for families and individuals in the Farmington, MN area. We saw how large dealerships often prioritized sales volume over customer relationships — and we chose a different path.
              </p>
              <p className="text-charcoal-soft font-sans text-base leading-relaxed mb-5">
                From day one, our approach has been to listen first. We ask questions because we genuinely care about finding the right vehicle for your lifestyle and budget — not just any vehicle on our lot. Our team members — Ahmed, Muhammad, and Chris — embody this philosophy in every interaction.
              </p>
              <blockquote className="border-l-4 border-gold-DEFAULT pl-6 py-3 my-7 bg-cream-DEFAULT rounded-r-xl">
                <p className="font-serif text-charcoal italic text-xl leading-relaxed">
                  &ldquo;It was so refreshing to go to a dealer where they ask you questions because they actually care about your needs.&rdquo;
                </p>
                <footer className="mt-3 text-sm text-charcoal-soft font-sans font-semibold">
                  — Aaron Rousu, Verified Customer
                </footer>
              </blockquote>
              <p className="text-charcoal-soft font-sans text-base leading-relaxed">
                Today, United Auto Group serves customers from across the Twin Cities south metro — from Apple Valley and Lakeville to Hastings and Northfield. Our 5-star Google rating and 47+ reviews reflect the relationships we have built with every customer who has trusted us with their next vehicle purchase.
              </p>
            </div>
            <div className="lg:w-1/2">
              <div className="w-full h-[420px] rounded-2xl overflow-hidden">
                <img
                  src={CAR_IMAGE}
                  alt="United Auto Group Farmington MN premium vehicles"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const values = [
  {
    icon: "ri-shield-check-line",
    title: "Integrity First",
    description: "We are transparent about every vehicle&apos;s condition, pricing, and financing terms. No hidden fees, no surprises, no pressure.",
  },
  {
    icon: "ri-user-heart-line",
    title: "Customer-Centered Care",
    description: "We ask questions and listen. Our goal is to find the right vehicle for you — and to stay in contact after the sale to ensure your satisfaction.",
  },
  {
    icon: "ri-award-line",
    title: "Quality Without Compromise",
    description: "Every vehicle in our inventory is hand-selected and inspected. We carry only the vehicles we would be proud to drive ourselves.",
  },
  {
    icon: "ri-community-line",
    title: "Community Commitment",
    description: "We are a local Farmington business serving our neighbors. We take our role in the community seriously and aim to be an asset to the area.",
  },
];

function ValueCard({ val, index }: { val: typeof values[0]; index: number }) {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`bg-white rounded-2xl p-7 border border-cream-dark transition-all duration-500 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="w-11 h-11 rounded-xl bg-gold-DEFAULT/12 flex items-center justify-center mb-5">
        <i className={`${val.icon} text-gold-DEFAULT text-xl`} />
      </div>
      <h3 className="font-serif text-charcoal text-xl mb-3">{val.title}</h3>
      <p className="text-charcoal-soft font-sans text-sm leading-relaxed">
        {val.description}
      </p>
    </div>
  );
}

function OurValues() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section
      className="bg-cream-DEFAULT py-20 md:py-28"
      aria-labelledby="values-heading"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          ref={ref}
          className={`text-center mb-12 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="inline-flex items-center gap-3 mb-5 justify-center">
            <span className="h-px w-10 bg-gold-DEFAULT" />
            <span className="text-gold-DEFAULT text-xs font-bold tracking-[0.22em] uppercase font-sans">
              OUR VALUES
            </span>
            <span className="h-px w-10 bg-gold-DEFAULT" />
          </div>
          <h2
            id="values-heading"
            className="font-serif text-charcoal"
            style={{ fontSize: "clamp(1.9rem, 3vw, 2.7rem)" }}
          >
            The Principles That Guide Everything We Do
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {values.map((val, i) => (
            <ValueCard key={val.title} val={val} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TeamMemberCard({ member, index }: { member: typeof teamMembers[0]; index: number }) {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`group bg-white rounded-2xl overflow-hidden border border-cream-dark transition-all duration-500 hover:-translate-y-1 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Photo area */}
      <div className="relative w-full h-64 overflow-hidden bg-cream-dark">
        <img
          src={member.photo}
          alt={`${member.name} — ${member.title} at United Auto Group Farmington MN`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
        {/* Bottom gradient for name overlap */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-charcoal/80 to-transparent" />
        {/* Title badge on photo */}
        <div className="absolute bottom-4 left-4 right-4">
          <p className="text-gold-DEFAULT text-xs font-bold font-sans tracking-[0.2em] uppercase mb-0.5">
            {member.title}
          </p>
          <h3 className="font-serif text-white text-2xl leading-tight">{member.name}</h3>
        </div>
      </div>
      {/* Description */}
      <div className="p-6">
        <p className="text-charcoal-soft font-sans text-sm leading-relaxed">
          {member.description}
        </p>
      </div>
    </div>
  );
}

function MeetTheTeam() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section
      className="bg-white py-20 md:py-28"
      aria-labelledby="team-heading"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div
          ref={ref}
          className={`text-center mb-12 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="inline-flex items-center gap-3 mb-5 justify-center">
            <span className="h-px w-10 bg-gold-DEFAULT" />
            <span className="text-gold-DEFAULT text-xs font-bold tracking-[0.22em] uppercase font-sans">
              MEET THE TEAM
            </span>
            <span className="h-px w-10 bg-gold-DEFAULT" />
          </div>
          <h2
            id="team-heading"
            className="font-serif text-charcoal"
            style={{ fontSize: "clamp(1.9rem, 3vw, 2.7rem)" }}
          >
            The People Behind Your Car-Buying Experience
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {teamMembers.map((member, i) => (
            <TeamMemberCard key={member.name} member={member} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <div className="font-sans">
      <Navbar />

      {/* 1. Hero */}
      <PageHero
        breadcrumb="Home › About Us"
        title="A Dealership Built on"
        titleAccent="Honesty & Community"
        subtitle="United Auto Group is Farmington, MN&apos;s trusted used car dealership. Meet the team, learn our story, and discover why hundreds of Twin Cities families choose us for their next vehicle."
        backgroundImage={HERO_IMAGE}
        primaryCTA={{ label: "View Our Inventory", href: "https://www.uagmn.com/inventory/" }}
        secondaryCTA={{ label: "Contact the Team", href: "#contact-form" }}
        fullScreen={false}
        showPhone
      />

      {/* 2. Trust Bar */}
      <TrustBar />

      {/* 3. Page-Unique: Story + Values + Team */}
      <OurStory />
      <OurValues />
      <MeetTheTeam />

      {/* 4. Services Overview */}
      <ServicesGrid
        title="What United Auto Group Offers Its Customers"
        subtitle="Beyond our team and values — here are the core services that make us Farmington&apos;s most trusted used car dealership."
        services={services}
        eyebrow="OUR SERVICES"
      />

      {/* 5. AI Answer Block */}
      <AIAnswerBlock
        title="Everything You Need to Know About United Auto Group"
        subtitle="Honest answers about who we are, our team, and why our customers keep coming back."
        items={aboutAIItems}
      />

      {/* 6. Process */}
      <ProcessSection
        title="Our Approach to Every Single Sale"
        subtitle="From first contact to final handshake — here is how United Auto Group delivers a car-buying experience unlike anything else in the Twin Cities."
      />

      {/* 7. Why Choose Us */}
      <WhyChooseUs
        title="Why Farmington Families Trust United Auto Group"
        description="Our 5-star reputation is not an accident. It is the result of a team that genuinely cares, a commitment to only stocking quality vehicles, and a philosophy that puts the customer&apos;s needs above everything else."
      />

      {/* 8. Service Areas */}
      <ServiceAreas
        title="Proudly Serving the Twin Cities South Metro"
        subtitle="We serve customers from across the region who have heard about our reputation and are willing to make the drive for a better car-buying experience."
      />

      {/* 9. Testimonials */}
      <TestimonialsSection />

      {/* 10. Lead Form */}
      <LeadForm />

      {/* 11. FAQ */}
      <FAQSection
        title="Questions About United Auto Group"
        items={aboutFAQ}
      />

      {/* 12. Final CTA */}
      <FinalCTA
        title="Experience the United Auto Group Difference"
        subtitle="We are not just here to sell you a car. We are here to build a relationship and help you find the right vehicle for your life. Come visit us in Farmington, MN today."
        primaryCTA={{ label: "Visit Our Dealership", href: "https://maps.app.goo.gl/Vm3ubU5UPaCURLk67" }}
      />

      <Footer />
    </div>
  );
}
